import { Stack } from '@mui/material';
import { InventoryTable } from '@Inventory/components/InventoryTable';
import { useInventoryResources } from '@Inventory/services/use-inventory-resources';
import { useMemo } from 'react';
import { EmptyState, Loader, MaterialIcon } from '@components';
import { ResourceNodeFieldsFragment } from '@api/gql/graphql';
import { getFragmentData } from '@api/gql';
import { useSearchParams } from 'react-router-dom';
import { PAGE_PARAM } from '@api';
import { InventoryTableRow } from '@Inventory/components/InventoryTableRow';
import { SaveInventoryQueryModal } from '@Inventory/organisms/SaveInventoryQueryModal';
import { InventoryDrawer } from '@Inventory/organisms/InventoryDrawer';
import { InventoryPageQueryKeys } from '@Inventory/common/queryKeys';
import SavedQueryFilter from '@Inventory/organisms/SavedQueryFilter';
import { useGetQueryQP } from '@Inventory/AqlBuilder/helpers/common';
import AqlBuilderFilters from '@Inventory/AqlBuilder/organisms/AqlBuilderFilters';
import SimplePagination from '@common/components/SimplePagination';
import { PERMISSIONS_RISK_LEVEL_SUMMARY_FIELDS_FRAGMENT, RESOURCE_NODE_FIELDS_FRAGMENT } from '@Inventory/services/gql';
import InventoryCounts from '@Inventory/organisms/InventoryCounts';
import { AlertBanner } from '@components/AlertBanner';
import { aponoColors } from '@utils';
import { EditInventoryQueryModal } from '@Inventory/organisms/EditInventoryQueryModal';

const DEFAULT_TAKE = 50;

export function InventoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const isSaveModalOpen = searchParams.get(InventoryPageQueryKeys.SaveQueryModalOpen) === 'true';
  const isEditModalOpen = searchParams.get(InventoryPageQueryKeys.EditQueryModalOpen) === 'true';

  const currentPage = Number(searchParams.get(PAGE_PARAM) ?? 1);
  const offset = (currentPage - 1) * DEFAULT_TAKE;
  const query = useGetQueryQP();
  const paginationInfo = { take: DEFAULT_TAKE, offset };

  const { data, isFetched, isFetching } = useInventoryResources({ query }, paginationInfo);

  const inventoryResources = useMemo(
    () =>
      data?.data.map((edge) => ({
        resource: getFragmentData(RESOURCE_NODE_FIELDS_FRAGMENT, edge.node.resource),
        entitlementsCount: edge.node.entitlementsCount ?? undefined,
        permissionsCount: edge.node.permissionsCount ?? undefined,
        permissionsRiskLevelSummary: edge.node.permissionsRiskLevelSummary
          ? getFragmentData(PERMISSIONS_RISK_LEVEL_SUMMARY_FIELDS_FRAGMENT, edge.node.permissionsRiskLevelSummary)
          : undefined,
      })) || [],
    [data],
  );

  const handlePageChange = (newPage: number) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set(PAGE_PARAM, String(newPage));
    setSearchParams(newSearchParams);
    window.scrollTo(0, 0);
  };

  const handleOnClick = (resource: ResourceNodeFieldsFragment) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set(InventoryPageQueryKeys.ResourceId, resource.id);
    setSearchParams(newSearchParams);
  };

  if (!isFetched) {
    return <Loader />;
  }

  if (!inventoryResources.length && !query) {
    return <EmptyState title="No Data Found" imgSrc="/static/EmptyStateImages/not-found-illustration.svg" />;
  }

  return (
    <>
      <Stack direction="column" spacing={2}>
        <AccessScopesBanner />
        <div>
          <SavedQueryFilter />
        </div>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-end" spacing={2}>
          <AqlBuilderFilters />
          <InventoryCounts type="resources" />
        </Stack>
        <InventoryTable isLoading={isFetching}>
          {inventoryResources.map((resource) => (
            <InventoryTableRow
              key={resource.resource.id}
              resource={resource.resource}
              entitlementsCount={resource.entitlementsCount}
              permissionsCount={resource.permissionsCount}
              permissionsRiskLevelSummary={resource.permissionsRiskLevelSummary}
              onClick={() => handleOnClick(resource.resource)}
            />
          ))}
        </InventoryTable>
        <SimplePagination
          hasPreviousPage={currentPage > 1}
          hasNextPage={!!data?.hasMore}
          isFetching={isFetching}
          onPreviousPage={() => handlePageChange(currentPage - 1)}
          onNextPage={() => handlePageChange(currentPage + 1)}
        />
      </Stack>
      {isSaveModalOpen && <SaveInventoryQueryModal open={isSaveModalOpen} />}
      {isEditModalOpen && <EditInventoryQueryModal open={isEditModalOpen} />}
      <InventoryDrawer />
    </>
  );
}

function AccessScopesBanner() {
  return (
    <AlertBanner
      type="outlinedInfo"
      icon={<MaterialIcon symbol="lightbulb" style={{ color: aponoColors.tertiary[300] }} />}
      message="Create an Access Scope to build a more precise and efficient Access Flow"
    />
  );
}
